import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { toast } from 'react-toastify';

import PriceBlock from '../common/PriceBlock';
import StockBlock from '../common/StockBlock';
import PhysicalSetBlock from '../common/PhysicalSetBlock';

import InputWrapper from '../../common/InputWrapper';
import Button from '../../common/Button';
import Select from '../../common/Select';

import MarginSalesBlock from '../../Settings/MarginSalesBlock';
import ProductsPriceMarginHistory from './../ProductsPriceMarginHistory';
import MarginSalesChart from '../../Summary/MarginSalesChart';

import { fetchGetSKUSets, fetchUpdateSKUSet } from '../../../actions/items';

import {
  preventSubmittingByEnter,
  getCurrentFeature,
  getVariantImageUrl,
  parseError,
  getEshopType,
  getProductEncodedUrl,
} from '../../../helpers';
import Checkbox from '../../common/Checkbox';
import ChestnyZnakInfoBlock from '../common/ChestnyZnakInfoBlock';
import MonoboxesBlock from '../common/MonoboxesBlock';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const FormWrapper = styled('form')`
  .header-title {
    margin-bottom: 25px;
  }
  .option-input {
    width: 95%;
    label {
      text-transform: capitalize;
    }
  }
  .variant-image {
    width: inherit;
    cursor: pointer;
  }
  .react-numeric-input {
    flex: 1 1;
    input {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  .dropdown-menu {
    position: absolute;
    will-change: transform;
    bottom: 100%;
    top: unset;
    left: 0;
    transform: translate3d(0px, 35px, 0px);

    &.show {
      bottom: 100%;
      top: unset !important;
    }

    .dropdown-item {
      cursor: pointer;
      padding-left: 5px;
      padding-right: 5px;
    }
  }

  .dropdown-toggle {
    border: 1px solid #e3e5e7;
  }
  .add-barcode-dropdown-shadow {
    position: fixed;
    opacity: 0.2;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    z-index: 1;
  }

  .form-group {
    position: relative;
  }

  .image {
    max-height: 300px;
    img {
      height: 100%;
      object-fit: contain;
    }
  }

  .options-list {
    position: absolute;
    top: 27px;
    right: 0;
    background: white;
    z-index: 1;
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 5px;
    list-style: none;
    width: 180px;

    .options-item {
      padding: 9px 19px;
      cursor: pointer;

      &:hover {
        background: #7e57c310;
      }
    }
  }

  .barcode-type-info {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 10px;
    margin: auto;
    height: fit-content;
    color: #7e57c2;
    pointer-events: none;
  }

  .barcode-type-info-first {
    top: 27px;
  }

  @media (max-width: 578px) {
    .image {
      max-height: 250px;
    }
  }

  .warehouse-dimensions {
    background: #f1f5f7;
    border-radius: 4px;
    padding: 6px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }
  .warehouse-dimensions-text {
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    color: #949cb5;
    margin-right: 20px;
    span {
      font-weight: normal;
    }
  }
  .btn-copy {
    display: flex;
    align-items: center;
    padding: 0;
    font-size: 12px;
    line-height: 20px;
    i {
      margin-right: 4px;
    }
  }

  @media (max-width: 1360px) and (min-width: 992px) {
    .warehouse-dimensions {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  @media (max-width: 600px) {
    .warehouse-dimensions {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  @media (max-width: 380px) {
    .warehouse-dimensions-text span {
      display: block;
    }
  }
`;

const ParametersWrapper = styled('div')`
  padding: 1.5rem 0;
  > div:not(.border-block) {
    margin-left: calc(1.5rem - 12px);
    margin-right: calc(1.5rem - 12px);
  }
  .border-block {
    border-top: 1px solid #f7f6f6;
  }

  .non-active-input {
    background-color: #f3f3f3;
  }
`;

const weightTypes = [
  { value: 'kg', label: 'кг' },
  { value: 'gr', label: 'гр' },
];

const getOptionInputType = (i, optionsUsed, features) => {
  const currentFeature = getCurrentFeature(optionsUsed, i, features);
  return currentFeature?.type === 'decimal' ? 'number' : 'text';
};

const getOptionInputValue = (i, optionsUsed, features) => {
  const currentFeature = getCurrentFeature(optionsUsed, i, features);
  if (currentFeature?.type === 'string') {
    return optionsUsed[i].stringValue;
  }
  if (currentFeature?.type === 'decimal') {
    return optionsUsed[i].decimalValue;
  }
};

const VariantData = (props) => {
  const {
    isArchived,
    isNew,
    isValid,
    product,
    variant,
    values,
    warehouses,
    setFieldValue,
    features,
    productId,
    history,
    loading,
    setLoading,
    fetchDeleteVariant,
    editVariant,
    handleSubmit,
    errors,
    showModal,
    setUnsavedStatus,
    handleExportMovements,
    refreshVariant,
    eshops,
    productsPriceMarginHistory,
    productMarginShipped,
    productMarginDelivered,
    haveProductsPriceMarginHistory,
    monoboxesUpdateRef,
  } = props;

  const dispatch = useDispatch();

  const [physicalSet, setPhysicalSet] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const { t } = useAutoTranslation();

  const adminMode = localStorage.getItem('adminPanel');

  const shouldShowWarehouseDimensionsAndWeight =
    variant?.weightFact &&
    variant?.dimensionsFact?.width &&
    variant?.dimensionsFact?.height &&
    variant?.dimensionsFact?.depth;

  const refreshPhysicalSet = async () => {
    const newData = await dispatch(fetchGetSKUSets({ id: variant.id }));
    setPhysicalSet(newData.physicalSets);
  };

  useEffect(() => {
    (async function () {
      if (!physicalSet && variant.isPhysicalSet) {
        await refreshPhysicalSet();
      }
    })();
  }, []);

  const handleDelete = () => {
    const url = getProductEncodedUrl(productId, undefined, { archived: isArchived });

    setLoading(true);
    if (!isNew) {
      fetchDeleteVariant(productId, variant.id)
        .then(() => {
          history.push(url);
        })
        .catch((err) => {
          toast.error(err.data.errors[0].message);
          setLoading(false);
        });
    } else {
      history.push(url);
    }
  };

  const handleChangeInput = (id, value) => {
    setFieldValue(id, value);
    setUnsavedStatus(true);
  };

  const handleChangeOptionInput = (i, code, value) => {
    const currentFeature = getCurrentFeature(values.optionsUsed, i, features);
    const newOptions = values.optionsUsed?.map((o) => {
      if (o.code === code) {
        if (currentFeature.type === 'string') {
          return { ...o, stringValue: value };
        }
        if (currentFeature.type === 'decimal') {
          return { ...o, decimalValue: value };
        }
      }
      return o;
    });
    setFieldValue('optionsUsed', newOptions);
    setUnsavedStatus(true);
  };

  const handleChangeDimensionsInput = (id, value) => {
    const newDimensions = Object.assign({}, values.dimensions, { [id]: value });
    setFieldValue('dimensions', newDimensions);
    setUnsavedStatus(true);
  };

  const handleChangeBarcodes = (e, index, reset = false) => {
    const newBarcodes = JSON.parse(JSON.stringify(values.barcodes));
    reset === false ? (newBarcodes[index].value = e.target.value) : (newBarcodes[index].value = e);
    handleChangeInput('barcodes', newBarcodes);
  };

  const handleRemoveBarcode = (index) => {
    const newBarcodes = JSON.parse(JSON.stringify(values.barcodes));
    newBarcodes.splice(index, 1);
    handleChangeInput('barcodes', newBarcodes);
  };

  const handleAddOneMoreBarcode = (type) => {
    const newBarcodes = JSON.parse(JSON.stringify(values.barcodes));
    newBarcodes.push({
      value: '',
      isDefault: false,
      type,
    });
    handleChangeInput('barcodes', newBarcodes);
    setDropdownVisible(false);
  };

  const handleActivityStatusBarcode = (value, index) => {
    const newBarcodes = JSON.parse(JSON.stringify(values.barcodes));
    newBarcodes[index].disabled = value;
    handleChangeInput('barcodes', newBarcodes);
  };

  const handleChangeWeightType = (value) => {
    if (values.weightType === 'kg' && value === 'gr') {
      setFieldValue('weight', Number(values.weight) * 1000);
    } else if (values.weightType === 'gr' && value === 'kg') {
      setFieldValue('weight', Number(values.weight) / 1000);
    }
    setFieldValue('weightType', value);
    setUnsavedStatus(true);
  };

  const handleVariantImageClick = () => {
    showModal('SetVariantImage', {
      images: product.images,
      productId,
      defaultImage: variant.defaultImage,
      onSave: (imageUrl) => {
        return editVariant(imageUrl);
      },
    });
  };

  const [eshopSettings, setEshopSettings] = useState({});

  useEffect(() => {
    if (values.eshopSettings?.length) {
      let newEshopSettings = {};
      values.eshopSettings.forEach((shop) => {
        newEshopSettings[shop.eshopId] = {
          ...shop,
        };
      });
      setEshopSettings(newEshopSettings);
    } else {
      setEshopSettings({});
    }
  }, [values.eshopSettings]);

  const checkIfShopExist = (id) => {
    return values.eshopSettings.find((shop) => shop.eshopId === id);
  };

  const removeShop = (id) => {
    return [...values.eshopSettings].filter((shop) => shop.eshopId !== id);
  };

  const handleChangeShopSettings = (type, id, value) => {
    let newEshopSettings = [];
    if (!value) {
      if (type === 'extId') {
        if (values.eshopSettings?.length) {
          const isExist = checkIfShopExist(id);
          if (isExist) {
            newEshopSettings = removeShop(id);
            handleChangeInput('eshopSettings', newEshopSettings);
          }
          return;
        }
      }
      if (type === 'enabled') {
        if (values.eshopSettings?.length) {
          const isExist = checkIfShopExist(id);
          if (isExist) {
            if (!isExist.extId) {
              newEshopSettings = removeShop(id);
              handleChangeInput('eshopSettings', newEshopSettings);
              return;
            }
          }
        }
      }
    }
    if (values.eshopSettings?.length) {
      if (type === 'enabled' && !value) {
        toast.info(t('Внимание! Скорректируйте остаток этого товара на маркетплейсе вручную'));
      }
      const isExist = checkIfShopExist(id);
      if (isExist) {
        newEshopSettings = [...values.eshopSettings].map((shop) => {
          if (shop.eshopId === id) {
            return {
              ...shop,
              [type]: value,
            };
          } else {
            return {
              ...shop,
            };
          }
        });
      } else {
        newEshopSettings = [
          ...values.eshopSettings,
          {
            eshopId: id,
            enabled: false,
            extId: '',
            [type]: value,
          },
        ];
      }
    } else {
      newEshopSettings = [
        {
          eshopId: id,
          enabled: false,
          extId: '',
          [type]: value,
        },
      ];
    }
    handleChangeInput('eshopSettings', newEshopSettings);
  };

  const handleCopyWeightAndDimensions = () => {
    if (variant?.weightFact) {
      if (values.weightType !== 'gr') {
        handleChangeWeightType('gr');
      }
      handleChangeInput('weight', variant.weightFact);
    }
    if (variant?.dimensionsFact) {
      setFieldValue('dimensions', {
        depth: variant.dimensionsFact?.depth ?? 0,
        height: variant.dimensionsFact?.height ?? 0,
        width: variant.dimensionsFact?.width ?? 0,
      });
    }
  };

  const [showDropdown, setShowDropdown] = useState(false);

  const openImportChestnyznakModal = () => {
    dispatch(
      showModal('ImportChestnyznakNumbersModal', {
        id: variant.id,
      }),
    );
  };

  return (
    <FormWrapper onKeyDown={preventSubmittingByEnter} onSubmit={handleSubmit}>
      <div className="card-box">
        <div className="row">
          <div className="col-12">
            <h4 className="header-title">{t('Выбранный вариант')}</h4>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-7">
            {variant.optionsUsed?.map((o, i) => (
              <div key={o.code} className="form-group option-input">
                <InputWrapper
                  type={getOptionInputType(i, values.optionsUsed, features)}
                  label={t(o.title)}
                  id={o.code}
                  value={getOptionInputValue(i, values.optionsUsed, features)}
                  onChange={(e) => handleChangeOptionInput(i, o.code, e.target.value)}
                />
              </div>
            ))}
          </div>
          <div className="col-md-3 col-sm-5 col-7 image">
            <img
              alt=""
              className="variant-image"
              src={getVariantImageUrl(variant, 250)}
              onClick={handleVariantImageClick}
            />
          </div>
        </div>
      </div>

      <PriceBlock
        currentProduct={product}
        values={values}
        handleChangeInput={handleChangeInput}
        showNdsField
      />

      <ParametersWrapper className="card-box">
        <div className="row">
          <div className="col-12">
            <h4 className="header-title">{t('Параметры')}</h4>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="form-group weight-group">
              <InputWrapper
                type="number"
                label={t('Вес')}
                id="weight"
                value={values.weight}
                error={errors.weight}
                onChange={(e) => handleChangeInput('weight', e.target.value)}
                textAppend={
                  <Select
                    options={weightTypes}
                    value={weightTypes.find((t) => t.value === values.weightType) || undefined}
                    onChange={(e) => handleChangeWeightType(e.value)}
                  />
                }
                placeholder={t('Введите вес')}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-4 col-md-4 col-lg-12 col-xl-4">
            <div className="form-group">
              <InputWrapper
                type="number"
                label={t('Ширина')}
                id="width"
                value={values.dimensions.width}
                error={errors.dimensions && errors.dimensions.width}
                onChange={(e) => handleChangeDimensionsInput('width', e.target.value)}
                textAppend={t('см')}
              />
            </div>
          </div>
          <div className="col-sm-4 col-md-4 col-lg-12 col-xl-4">
            <div className="form-group">
              <InputWrapper
                type="number"
                label={t('Высота')}
                id="height"
                value={values.dimensions.height}
                error={errors.dimensions && errors.dimensions.height}
                onChange={(e) => handleChangeDimensionsInput('height', e.target.value)}
                textAppend={t('см')}
              />
            </div>
          </div>
          <div className="col-sm-4 col-md-4 col-lg-12 col-xl-4">
            <div className="form-group">
              <InputWrapper
                type="number"
                label={t('Глубина')}
                id="depth"
                value={values.dimensions.depth}
                error={errors.dimensions && errors.dimensions.depth}
                onChange={(e) => handleChangeDimensionsInput('depth', e.target.value)}
                textAppend={t('см')}
              />
            </div>
          </div>
        </div>

        {shouldShowWarehouseDimensionsAndWeight ? (
          <div className="row">
            <div className="col-12">
              <div className="warehouse-dimensions">
                <span className="warehouse-dimensions-text">
                  {t('Текущие габариты и вес')}:{' '}
                  <span>
                    {' '}
                    {variant?.dimensionsFact
                      ? `${variant.dimensionsFact.width ?? 0} x ${
                          variant.dimensionsFact.height ?? 0
                        } x ${variant.dimensionsFact.depth ?? 0} см`
                      : null}{' '}
                    {variant?.dimensionsFact && variant?.weightFact ? ` | ` : null}{' '}
                    {variant?.weightFact ? `${variant.weightFact} гр` : null}{' '}
                  </span>
                </span>
                <Button
                  classType="btn-link btn-copy"
                  type="button"
                  text={
                    <span>
                      <i className="fe-copy" style={{ color: '#7e57c2', fontSize: '14px' }} />
                      {t('Скопировать и подставить')}
                    </span>
                  }
                  onClick={handleCopyWeightAndDimensions}
                />
              </div>
            </div>
          </div>
        ) : null}

        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <InputWrapper
                type="text"
                label={t('Артикул')}
                id="article"
                value={values.article}
                error={errors.article}
                onChange={(e) => handleChangeInput('article', e.target.value)}
                placeholder={t('Введите артикул')}
              />
            </div>
          </div>
        </div>
        {values.barcodes.length > 0 && (
          <div className="row">
            <div className="col-10">
              <div className="form-group">
                <InputWrapper
                  type="text"
                  label={t('Штрихкод')}
                  id="barcode"
                  value={values.barcodes[0].value}
                  error={errors.barcodes}
                  onChange={(e) => handleChangeBarcodes(e, 0)}
                  placeholder={t('Введите штрихкод')}
                  addButton
                  addButtonText={t('Еще штриход')}
                  onAddButtonClick={() => setDropdownVisible(true)}
                  inputClassName={
                    values?.barcodes[0].disabled ? 'non-active-input form-control' : false
                  }
                />
                {values.barcodes[0].type === `COMMON` && (
                  <span className="barcode-type-info barcode-type-info-first">{t('Обычный')}</span>
                )}
                {values.barcodes[0].type === `WILDBERRIES` && (
                  <span className="barcode-type-info barcode-type-info-first">Wildberries</span>
                )}
                {values.barcodes[0].type === `OZON` && (
                  <span className="barcode-type-info barcode-type-info-first">OZON</span>
                )}
                {values.barcodes[0].type === `SBERMEGAMARKET` && (
                  <span className="barcode-type-info barcode-type-info-first">СберМегаМаркет</span>
                )}
                {values.barcodes[0].type === `LETOILE` && (
                  <span className="barcode-type-info barcode-type-info-first">Лэтуаль</span>
                )}
                {values.barcodes[0].type === `ZOLOTOE_YABLOKO` && (
                  <span className="barcode-type-info barcode-type-info-first">Золотое Яблоко</span>
                )}
                {values.barcodes[0].type === `YANDEX_MARKET` && (
                  <span className="barcode-type-info barcode-type-info-first">Яндекс Маркет</span>
                )}
                {values.barcodes[0].type === `LAMODA` && (
                  <span className="barcode-type-info barcode-type-info-first">Lamoda</span>
                )}
                {values.barcodes[0].type === `DET_MIR` && (
                  <span className="barcode-type-info barcode-type-info-first">Детский мир</span>
                )}
                {values.barcodes[0].type === `VSEINSTRUMENTI` && (
                  <span className="barcode-type-info barcode-type-info-first">Все инструменты</span>
                )}
                {values.barcodes[0].type === `RETAIL_NETWORK` && ( //ZDRAV_CITY
                  <span className="barcode-type-info barcode-type-info-first">Здравсити</span>
                )}
                {values.barcodes[0].type === `RETAIL_NETWORK_4` && (
                  <span className="barcode-type-info barcode-type-info-first">
                    {t('Розничные сети групповой ШК')} 4
                  </span>
                )}
                {values.barcodes[0].type === `RETAIL_NETWORK_6` && (
                  <span className="barcode-type-info barcode-type-info-first">
                    {t('Розничные сети групповой ШК')} 6
                  </span>
                )}
                {values.barcodes[0].type === `RETAIL_NETWORK_24` && (
                  <span className="barcode-type-info barcode-type-info-first">
                    {t('Розничные сети групповой ШК')} 24
                  </span>
                )}

                {dropdownVisible && (
                  <>
                    <span
                      className="add-barcode-dropdown-shadow"
                      onClick={() => setDropdownVisible(false)}
                    />
                    <ul className="options-list">
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`COMMON`)}
                      >
                        {t('Обычный')}
                      </li>
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`WILDBERRIES`)}
                      >
                        Wildberries
                      </li>
                      <li className="options-item" onClick={() => handleAddOneMoreBarcode(`OZON`)}>
                        OZON
                      </li>
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`SBERMEGAMARKET`)}
                      >
                        СберМегаМаркет
                      </li>
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`LETOILE`)}
                      >
                        Лэтуаль
                      </li>
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`ZOLOTOE_YABLOKO`)}
                      >
                        Золотое Яблоко
                      </li>
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`YANDEX_MARKET`)}
                      >
                        Яндекс Маркет
                      </li>
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`LAMODA`)}
                      >
                        Lamoda
                      </li>
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`DET_MIR`)}
                      >
                        Детский мир
                      </li>
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`VSEINSTRUMENTI`)}
                      >
                        Все инструменты
                      </li>
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`RETAIL_NETWORK_4`)}
                      >
                        {t('Розничные сети групповой ШК')} 4
                      </li>
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`RETAIL_NETWORK_6`)}
                      >
                        {t('Розничные сети групповой ШК')} 6
                      </li>
                      <li
                        className="options-item"
                        onClick={() => handleAddOneMoreBarcode(`RETAIL_NETWORK_24`)}
                      >
                        {t('Розничные сети групповой ШК')} 24
                      </li>
                    </ul>
                  </>
                )}
              </div>
            </div>
            <div
              className="col-2 removing-block"
              style={{
                display: 'flex',
                padding: '0',
                alignItems: 'center',
                maxWidth: '60px',
                marginTop: '10px',
              }}
            >
              {adminMode === 'true' ? (
                <span className="help-tooltip-wrapper">
                  <Checkbox
                    id={values.barcodes[0].value}
                    inputClass="admin-checkbox-bg-color fe-help-circle"
                    checked={values.barcodes[0].disabled}
                    onChange={(e) => handleActivityStatusBarcode(e.target.checked, 0)}
                  />
                  <span className="help-tooltip" style={{ minWidth: '70px', left: '-50px' }}>
                    {values.barcodes[0].disabled ? t('Неактивен') : t('Активен')}
                  </span>
                </span>
              ) : null}
              <Button
                style={{
                  marginLeft: 0,
                  paddingLeft: '13px',
                  marginLeft: 'auto',
                  paddingRight: 0,
                }}
                classType="btn-link"
                type="button"
                text={<i className="fe-trash-2" style={{ color: '#6c757d', fontSize: '20px' }} />}
                onClick={
                  values.barcodes.length === 1
                    ? () => handleChangeBarcodes('', 0, true)
                    : () => handleRemoveBarcode(0)
                }
              />
            </div>
          </div>
        )}

        {values.barcodes.length >= 1 ? (
          <Fragment>
            {values.barcodes.map((b, i) => (
              <Fragment key={`barcode-${i}`}>
                {i !== 0 ? (
                  <div className="row">
                    <div className="col-10">
                      <div className="form-group">
                        <InputWrapper
                          type="text"
                          noLabel
                          id="barcode"
                          value={values?.barcodes[i].value}
                          error={errors.barcodes}
                          onChange={(e) => handleChangeBarcodes(e, i)}
                          placeholder={t('Введите штрихкод')}
                          inputClassName={
                            values?.barcodes[i].disabled ? 'non-active-input form-control' : false
                          }
                        />
                        {values.barcodes[i].type === `COMMON` && (
                          <span className="barcode-type-info">{t('Обычный')}</span>
                        )}
                        {values.barcodes[i].type === `WILDBERRIES` && (
                          <span className="barcode-type-info">Wildberries</span>
                        )}
                        {values.barcodes[i].type === `OZON` && (
                          <span className="barcode-type-info">OZON</span>
                        )}
                        {values.barcodes[i].type === `SBERMEGAMARKET` && (
                          <span className="barcode-type-info">СберМегаМаркет</span>
                        )}
                        {values.barcodes[i].type === `LETOILE` && (
                          <span className="barcode-type-info">Лэтуаль</span>
                        )}
                        {values.barcodes[i].type === `ZOLOTOE_YABLOKO` && (
                          <span className="barcode-type-info">Золотое Яблоко</span>
                        )}
                        {values.barcodes[i].type === `YANDEX_MARKET` && (
                          <span className="barcode-type-info">Яндекс Маркет</span>
                        )}
                        {values.barcodes[i].type === `LAMODA` && (
                          <span className="barcode-type-info">Lamoda</span>
                        )}
                        {values.barcodes[i].type === `DET_MIR` && (
                          <span className="barcode-type-info">Детский мир</span>
                        )}
                        {values.barcodes[i].type === `VSEINSTRUMENTI` && (
                          <span className="barcode-type-info">Все инструменты</span>
                        )}
                        {values.barcodes[i].type === `RETAIL_NETWORK_4` && (
                          <span className="barcode-type-info">
                            {t('Розничные сети групповой ШК')} 4
                          </span>
                        )}
                        {values.barcodes[i].type === `RETAIL_NETWORK_6` && (
                          <span className="barcode-type-info">
                            {t('Розничные сети групповой ШК')} 6
                          </span>
                        )}
                        {values.barcodes[i].type === `RETAIL_NETWORK_24` && (
                          <span className="barcode-type-info">
                            {t('Розничные сети групповой ШК')} 24
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className="col-2 removing-block"
                      style={{
                        display: 'flex',
                        padding: '0',
                        marginTop: '-15px',
                        alignItems: 'center',
                        maxWidth: '60px',
                      }}
                    >
                      {adminMode === 'true' ? (
                        <span className="help-tooltip-wrapper">
                          <Checkbox
                            id={values.barcodes[i].value}
                            inputClass="admin-checkbox-bg-color fe-help-circle"
                            checked={values.barcodes[i].disabled}
                            onChange={(e) => handleActivityStatusBarcode(e.target.checked, i)}
                          />
                          <span
                            className="help-tooltip"
                            style={{ minWidth: '70px', left: '-50px' }}
                          >
                            {values.barcodes[i].disabled ? t('Неактивен') : t('Активен')}
                          </span>
                        </span>
                      ) : null}
                      <Button
                        style={{
                          marginLeft: 0,
                          paddingLeft: '13px',
                          marginLeft: 'auto',
                          paddingRight: 0,
                        }}
                        classType="btn-link"
                        type="button"
                        text={
                          <i
                            className="fe-trash-2"
                            style={{ color: '#6c757d', fontSize: '20px' }}
                          />
                        }
                        onClick={() => handleRemoveBarcode(i)}
                      />
                    </div>
                  </div>
                ) : null}
              </Fragment>
            ))}
          </Fragment>
        ) : null}

        {values.stock && values.stock.length ? (
          <StockBlock
            stock={values.stock}
            variant={variant}
            warehouses={warehouses}
            handleChangeInput={handleChangeInput}
            handleSubmit={handleSubmit}
            product={product}
          />
        ) : null}

        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <InputWrapper
                type="text"
                label={t('Бренд')}
                id="brandName"
                value={product.brandName}
                readOnly
              />
            </div>
          </div>
        </div>

        <div className="border-block" style={{ marginBottom: '18px' }} />

        {!isNew ? (
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <InputWrapper
                  type="text"
                  label="ID"
                  id="variantId"
                  value={variant.id}
                  placeholder={t('Введите внешний ID')}
                  readOnly
                />
              </div>
            </div>
          </div>
        ) : null}

        {eshops && eshops.length ? (
          <div className="row">
            <div className="col-12">
              {eshops.map((shop) => (
                <div className="form-group" key={shop.id}>
                  <div className="d-flex justify-content-between flex-md-row flex-column">
                    <label className="mb-md-0 mb-2" htmlFor={shop.id}>
                      {t('Внешний ID для')} {shop.name ? `"${shop.name}"` : null}{' '}
                      {shop.type ? `(${getEshopType(shop.type)})` : null}
                    </label>
                    <Checkbox
                      label={t('Обнулить остаток')}
                      id={`checkbox-zeroStock-${shop.id}`}
                      checked={eshopSettings[shop.id] ? eshopSettings[shop.id].zeroStock : false}
                      onChange={(e) =>
                        handleChangeShopSettings('zeroStock', shop.id, e.target.checked)
                      }
                      wrapperStyle={{ margin: '0 20px 0 auto' }}
                    />
                    <Checkbox
                      label={t('Разместить на площадке')}
                      id={`checkbox-enabled-${shop.id}`}
                      checked={eshopSettings[shop.id] ? eshopSettings[shop.id].enabled : false}
                      onChange={(e) =>
                        handleChangeShopSettings('enabled', shop.id, e.target.checked)
                      }
                    />
                  </div>
                  <InputWrapper
                    type="text"
                    id={shop.id}
                    value={eshopSettings[shop.id]?.extId || ''}
                    onChange={(e) => handleChangeShopSettings('extId', shop.id, e.target.value)}
                    placeholder={`${t('Введите внешний ID для')} ${shop.name}`}
                  />

                  <div className="row">
                    <div className="id-input-wrapper col-sm-4 col-md-4 col-lg-12 col-xl-4">
                      <label className="mb-md-0 mb-2" htmlFor={shop.id}>
                        {t('Процент остатков')}
                      </label>
                      <div className="percent-stock-wrapper">
                        <InputWrapper
                          type="text"
                          id={shop.id}
                          value={eshopSettings[shop.id]?.percentStock || ''}
                          onChange={(e) =>
                            handleChangeShopSettings('percentStock', shop.id, e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="id-input-wrapper ">
                      <label className="mb-md-0 mb-2" htmlFor={shop.id}>
                        {t('Выгружать последние N шт в данный магазин')}
                      </label>
                      <div className="percent-stock-wrapper col-sm-4 col-md-4 col-lg-12 col-xl-4 p-0">
                        <InputWrapper
                          type="text"
                          id={shop.id}
                          value={eshopSettings[shop.id]?.lastStock || ''}
                          onChange={(e) =>
                            handleChangeShopSettings('lastStock', shop.id, e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <InputWrapper
                type="text"
                label={t('Внешний ID')}
                id="extId"
                value={values.extId}
                error={errors.extId}
                onChange={(e) => handleChangeInput('extId', e.target.value)}
                placeholder={t('Введите внешний ID')}
              />
            </div>
            <div className="form-group">
              <InputWrapper
                type="text"
                label={`${t('Внешний ID')} 2`}
                id="extId2"
                value={values.extId2}
                error={errors.extId2}
                onChange={(e) => handleChangeInput('extId2', e.target.value)}
                placeholder={`${t('Введите внешний ID')} 2`}
              />
            </div>
            <div className="form-group">
              <InputWrapper
                type="text"
                label={`${t('Внешний ID')} 3`}
                id="extId3"
                value={values.extId3}
                error={errors.extId3}
                onChange={(e) => handleChangeInput('extId3', e.target.value)}
                placeholder={`${t('Введите внешний ID')} 3`}
              />
            </div>
            <div className="form-group">
              <InputWrapper
                type="text"
                label={`${t('Внешний ID')} 4`}
                id="extId4"
                value={values.extId4}
                error={errors.extId4}
                onChange={(e) => handleChangeInput('extId4', e.target.value)}
                placeholder={`${t('Введите внешний ID')} 4`}
              />
            </div>
            <div className="form-group">
              <InputWrapper
                type="text"
                label={`${t('Внешний ID')} 5`}
                id="extId5"
                value={values.extId5}
                error={errors.extId5}
                onChange={(e) => handleChangeInput('extId5', e.target.value)}
                placeholder={`${t('Введите внешний ID')} 5`}
              />
            </div>
          </div>
        </div>
      </ParametersWrapper>

      <div className="card-box">
        <MarginSalesBlock
          title={t('Маржинальность продаж')}
          values={values}
          handleChangeInput={handleChangeInput}
          productId={productId}
          variantId={variant?.id}
        />
      </div>

      {haveProductsPriceMarginHistory ? (
        <ProductsPriceMarginHistory productsPriceMarginHistory={productsPriceMarginHistory} />
      ) : null}

      {productMarginShipped || productMarginDelivered ? (
        <div className="row">
          <MarginSalesChart
            title={t('Маржинальность продаж')}
            shipped={productMarginShipped}
            delivered={productMarginDelivered}
          />
        </div>
      ) : null}

      <div className="card-box">
        <ChestnyZnakInfoBlock
          currentProductId={product.id}
          isProductPage={false}
          variantId={variant?.id}
          variantStockAvailable={variant?.stock[0].stockAvailable}
        />
      </div>

      <MonoboxesBlock
        product={product}
        variant={variant} // Variative Product
        setUnsavedStatus={setUnsavedStatus}
        monoboxesUpdateRef={monoboxesUpdateRef}
        refreshVariant={refreshVariant}
      />

      <div className="row variant-data-buttons" style={{ margin: 0 }}>
        <Button
          type="submit"
          text={t('Сохранить')}
          disabled={loading || !isValid}
          loading={loading}
        />
        <div style={{ position: `relative` }} className="ml-3">
          <button
            type="button"
            className="btn btn-light dropdown-toggle"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <>
              {t('Действия')}
              <i className="mdi mdi-chevron-up" />
            </>
          </button>
          <div
            className={`dropdown-menu ${showDropdown ? `show` : ``}`}
            onClick={() => setShowDropdown(false)}
          >
            <div className="dropdown-item" onClick={() => handleExportMovements(variant.id)}>
              <i className="fe-arrow-down mr-2" />
              {t('Экспорт')}
            </div>
            <div className="dropdown-item" onClick={() => openImportChestnyznakModal()}>
              <i className="fe-arrow-up mr-2" />
              {t('Импорт пула номеров «Честный Знак»')}
            </div>
          </div>
        </div>
        {!isNew && product.variants.length > 1 ? (
          <button
            type="button"
            className="btn btn-danger waves-effect waves-light"
            onClick={handleDelete}
            style={{ marginLeft: 'auto' }}
          >
            {t('Удалить')}
          </button>
        ) : null}
      </div>
    </FormWrapper>
  );
};

export default VariantData;
